import React, { useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_SEC = 300; // 5 min hard cap

/**
 * Winamp-skinned voicemail recorder. Same MediaRecorder state machine as
 * the standard build - only the chrome is different.
 *
 * The skin ships as pure CSS: a purple/blue chrome titlebar, a black LCD
 * with green pixel text, a stereo VU meter animated from the live mic
 * amplitude (falls back to a fake bounce if AnalyserNode is unavailable),
 * and 90s beveled silver buttons. The classic Play control is replaced
 * with a red RECORD button, Stop stops, Send uploads. Everything else
 * (kbps/kHz badges, seek slider, SHUFFLE / REPEAT tabs) is decorative.
 */
const VoicemailRecorder = ({ onDone }) => {
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState('');
  const [alias, setAlias] = useState('');
  const [message, setMessage] = useState('');
  const [mime, setMime] = useState('audio/webm');
  const [website, setWebsite] = useState('');
  const [levels, setLevels] = useState({ l: 0, r: 0 });
  const [isPlaying, setIsPlaying] = useState(false);

  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  const pickMime = () => {
    const opts = [
      'audio/webm;codecs=opus', 'audio/webm',
      'audio/mp4;codecs=mp4a', 'audio/mp4', 'audio/ogg;codecs=opus',
    ];
    for (const t of opts) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(t)) return t;
    }
    return '';
  };

  const startVuLoop = (stream) => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        let lo = 0, hi = 0;
        const mid = Math.floor(data.length / 2);
        for (let i = 0; i < mid; i++) lo += data[i];
        for (let i = mid; i < data.length; i++) hi += data[i];
        setLevels({
          l: Math.min(1, (lo / mid) / 180),
          r: Math.min(1, (hi / (data.length - mid)) / 180),
        });
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      // Fallback: fake bounce
      const start = performance.now();
      const tick = () => {
        const t = (performance.now() - start) / 1000;
        setLevels({ l: 0.35 + 0.3 * Math.abs(Math.sin(t * 3)), r: 0.35 + 0.3 * Math.abs(Math.cos(t * 2.4)) });
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    }
  };

  const start = async () => {
    setError('');
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setError('Your browser does not support in-page recording. Try Chrome, Safari 14+ or Firefox.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const type = pickMime();
      setMime(type || 'audio/webm');
      const rec = type ? new MediaRecorder(stream, { mimeType: type }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const b = new Blob(chunksRef.current, { type: type || 'audio/webm' });
        setBlob(b);
        setBlobUrl(URL.createObjectURL(b));
        setPhase('stopped');
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        setLevels({ l: 0, r: 0 });
      };
      recRef.current = rec;
      rec.start();
      startVuLoop(stream);
      setPhase('recording');
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((s) => {
          if (s + 1 >= MAX_SEC) { try { rec.stop(); } catch {} clearInterval(timerRef.current); return MAX_SEC; }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      setError(e.message || 'Could not access the microphone.');
    }
  };

  const stop = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { recRef.current?.stop(); } catch {}
  };

  const scrap = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null); setBlobUrl(''); setElapsed(0); setPhase('idle');
    setIsPlaying(false);
  };

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setIsPlaying(true); } else { a.pause(); setIsPlaying(false); }
  };

  const submit = async () => {
    if (!blob) return;
    setPhase('submitting');
    setError('');
    try {
      const contentType = mime.split(';')[0] || 'audio/webm';
      const presRes = await fetch(`${API}/api/voicemail/presign`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content_type: contentType }),
      });
      if (!presRes.ok) throw new Error((await presRes.json().catch(() => ({}))).detail || 'Could not prepare upload.');
      const { upload_url, key, public_url } = await presRes.json();
      const put = await fetch(upload_url, { method: 'PUT', headers: { 'Content-Type': contentType }, body: blob });
      if (!put.ok) throw new Error(`Upload failed (${put.status}).`);
      const save = await fetch(`${API}/api/voicemail`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, public_url, duration_sec: elapsed, size_bytes: blob.size,
          alias: alias.trim() || null, message: message.trim() || null, website }),
      });
      if (!save.ok) throw new Error((await save.json().catch(() => ({}))).detail || 'Could not save voicemail.');
      setPhase('done');
      setTimeout(() => onDone?.(), 900);
    } catch (e) { setError(e.message); setPhase('stopped'); }
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const trackTitle = phase === 'recording'
    ? '1. VOICEMAIL LINE - RECORDING...'
    : phase === 'stopped'
      ? `1. VOICEMAIL LINE - ${mmss(elapsed)} READY TO SEND`
      : phase === 'submitting'
        ? '1. VOICEMAIL LINE - UPLOADING...'
        : phase === 'done'
          ? '1. VOICEMAIL LINE - SENT. TRANSMISSION COMPLETE'
          : '1. VOICEMAIL LINE - TAP RECORD';

  // Marquee looping for the track title
  const [scrollX, setScrollX] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setScrollX((x) => (x + 1) % 400), 60);
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid="voicemail-recorder" data-skin="winamp" className="mx-auto" style={{ maxWidth: 520 }}>
      {/* ============ WINAMP CHROME ============ */}
      <div
        className="rounded-[3px] select-none"
        style={{
          background: 'linear-gradient(180deg, #6f7fa8 0%, #4a5a82 30%, #2c3a5a 100%)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -1px 0 rgba(0,0,0,0.5)',
          padding: '3px',
          fontFamily: '"Arial Narrow", Arial, sans-serif',
        }}
      >
        {/* Titlebar */}
        <div
          className="flex items-center h-[16px] px-1.5"
          style={{
            background: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.12) 0px, rgba(0,0,0,0.12) 1px, transparent 1px, transparent 2px), linear-gradient(180deg, #3a4870 0%, #232c48 100%)',
            borderRadius: '2px 2px 0 0',
          }}
        >
          <span aria-hidden className="text-yellow-300 text-[10px] font-black italic mr-1" style={{ textShadow: '0 1px 0 #000' }}>&Lambda;</span>
          {/* stripey drag bars */}
          <div className="flex-1 h-[7px] mx-1 opacity-70" style={{
            background: 'repeating-linear-gradient(0deg, #a8b4d0 0px, #a8b4d0 1px, transparent 1px, transparent 2px)',
          }} />
          <span
            className="text-[10px] font-black text-yellow-300 tracking-[0.15em]"
            style={{ fontFamily: 'Verdana, Geneva, sans-serif', textShadow: '0 1px 0 rgba(0,0,0,0.9)' }}
          >
            WINAMP
          </span>
          <div className="flex-1 h-[7px] mx-1 opacity-70" style={{
            background: 'repeating-linear-gradient(0deg, #a8b4d0 0px, #a8b4d0 1px, transparent 1px, transparent 2px)',
          }} />
          {['_', '□', '×'].map((c, i) => (
            <span
              key={i}
              aria-hidden
              className="inline-flex items-center justify-center text-[10px] text-zinc-200 w-[10px] h-[10px] ml-0.5"
              style={{
                background: 'linear-gradient(180deg,#586786,#2c3652)',
                border: '1px solid #1a2035',
                boxShadow: 'inset 1px 1px 0 rgba(255,255,255,0.2)',
              }}
            >
              {c}
            </span>
          ))}
        </div>

        {/* Body */}
        <div
          className="p-2 pt-2.5"
          style={{
            background: 'linear-gradient(180deg,#4d5c85 0%,#2f3d5c 100%)',
            borderRadius: '0 0 2px 2px',
          }}
        >
          {/* LCD row: OUTLAW label + timer + VU */}
          <div className="flex gap-2 mb-1.5">
            {/* Vertical OUTLAW letters (like the OAIDIY in the original) */}
            <div
              className="text-[9px] leading-[1.05] font-black text-yellow-300 tracking-tight select-none pt-0.5"
              style={{ writingMode: 'vertical-rl', textOrientation: 'upright', letterSpacing: '2px', textShadow: '0 1px 0 rgba(0,0,0,0.9)' }}
              aria-hidden
            >
              OUTLAW
            </div>

            {/* LCD block */}
            <div
              className="flex-1 relative rounded-[2px]"
              style={{
                background: '#0a0e18',
                boxShadow: 'inset 0 0 0 1px #1a2233, inset 0 2px 4px rgba(0,0,0,0.7)',
                padding: '4px 8px',
                fontFamily: '"Courier New", ui-monospace, monospace',
              }}
            >
              <div className="flex items-center gap-3">
                {/* VU meters */}
                <div className="flex gap-[3px] items-end h-[26px]" data-testid="winamp-vu">
                  {[levels.l, levels.r].map((lv, i) => (
                    <div key={i} className="w-[8px] h-full relative flex flex-col-reverse gap-[1px]">
                      {Array.from({ length: 14 }).map((_, j) => {
                        const on = j / 14 < lv;
                        const c = j < 8 ? '#33ff66' : j < 11 ? '#ccff33' : '#ff6633';
                        return (
                          <span
                            key={j}
                            aria-hidden
                            className="block h-[1px]"
                            style={{
                              background: on ? c : 'rgba(80,255,128,0.08)',
                              boxShadow: on ? `0 0 3px ${c}` : 'none',
                              width: '100%',
                            }}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>

                {/* Timer */}
                <div
                  className="ml-auto text-[26px] leading-none font-black tabular-nums"
                  style={{
                    color: phase === 'recording' ? '#ff3838' : '#33ff66',
                    textShadow: phase === 'recording' ? '0 0 6px #ff3838' : '0 0 6px #33ff66',
                    fontFamily: '"Courier New", ui-monospace, monospace',
                  }}
                  data-testid="winamp-timer"
                >
                  {mmss(elapsed)}
                </div>
              </div>
            </div>
          </div>

          {/* Track title + bitrate row */}
          <div className="flex gap-2 mb-1">
            <div
              className="flex-1 overflow-hidden rounded-[2px]"
              style={{ background: '#0a0e18', boxShadow: 'inset 0 0 0 1px #1a2233', padding: '3px 6px' }}
            >
              <div
                className="whitespace-nowrap text-[11px] font-black tracking-wider"
                style={{
                  color: '#33ff66',
                  fontFamily: '"Courier New", ui-monospace, monospace',
                  textShadow: '0 0 4px #33ff66',
                  transform: `translateX(${-scrollX}px)`,
                }}
                data-testid="winamp-track-title"
              >
                {trackTitle}{'  '}★{'  '}{trackTitle}
              </div>
            </div>
          </div>

          <div className="flex gap-2 mb-2 items-center text-[9px] font-black">
            <BitBadge label="16 kbps" />
            <BitBadge label="48 kHz" />
            <span className="text-zinc-400">mono</span>
            <span className="text-[#33ff66] font-black" style={{ textShadow: '0 0 3px #33ff66' }}>stereo</span>
            <div className="flex-1" />
            <TabBadge label="EQ" />
            <TabBadge label="PL" />
          </div>

          {/* Faux seek slider */}
          <div className="mb-2 h-[8px] rounded-[3px] relative"
            style={{ background: 'linear-gradient(180deg,#1a2035,#2a3352)', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.7)' }}
          >
            <div
              className="absolute top-1/2 -translate-y-1/2 h-[14px] w-[14px] rounded-[2px]"
              style={{
                left: `${Math.min(100, (elapsed / MAX_SEC) * 100)}%`,
                transform: 'translate(-50%, -50%)',
                background: 'linear-gradient(180deg,#c6cbde,#7c85a3)',
                boxShadow: 'inset 1px 1px 0 #fff, inset -1px -1px 0 #1a2035, 0 1px 2px rgba(0,0,0,0.6)',
              }}
            />
          </div>

          {/* Big instruction line so first-timers know what to tap. */}
          <div
            data-testid="winamp-hint"
            className="mt-1 mb-2 flex items-center gap-2 rounded-[2px] px-2 py-1.5"
            style={{
              background: '#0a0e18',
              boxShadow: 'inset 0 0 0 1px #1a2233',
              fontFamily: '"Courier New", monospace',
              color: phase === 'recording' ? '#ff5555' : phase === 'stopped' ? '#ffcc33' : phase === 'submitting' ? '#ccff33' : '#33ff66',
              textShadow: phase === 'recording' ? '0 0 3px #ff5555' : phase === 'stopped' ? '0 0 3px #ffcc33' : phase === 'submitting' ? '0 0 3px #ccff33' : '0 0 3px #33ff66',
            }}
          >
            <span className="inline-block h-2 w-2 rounded-full animate-pulse" style={{
              background: phase === 'recording' ? '#ff5555' : phase === 'stopped' ? '#ffcc33' : phase === 'submitting' ? '#ccff33' : '#33ff66',
              boxShadow: '0 0 4px currentColor',
            }} />
            <span className="text-[11px] font-black uppercase tracking-widest">
              {phase === 'idle' && 'Tap the RED button to start recording'}
              {phase === 'recording' && 'Recording · tap STOP when finished'}
              {phase === 'stopped' && 'Tap SEND to submit · or REDO to try again'}
              {phase === 'submitting' && 'Uploading your voicemail…'}
              {phase === 'done' && 'Transmission complete'}
            </span>
          </div>

          {/* Transport row: giant labelled buttons - only ever the action(s)
              relevant to the current phase so the user can't hit the wrong
              one. Each is icon + text, colour-coded (red = record/stop,
              green = send, silver = preview/redo). */}
          <div className="flex items-stretch gap-1.5" data-testid="winamp-transport">
            {phase === 'idle' && (
              <BigWinampButton
                onClick={start}
                testId="voicemail-start"
                label="Record"
                sublabel="Start"
                variant="red"
                icon={<RecordIcon big />}
                fullWidth
              />
            )}

            {phase === 'recording' && (
              <BigWinampButton
                onClick={stop}
                testId="voicemail-stop"
                label="Stop"
                sublabel="Finish"
                variant="red"
                pulsing
                icon={<StopIcon big />}
                fullWidth
              />
            )}

            {(phase === 'stopped' || phase === 'submitting') && (
              <>
                <BigWinampButton
                  onClick={togglePlay}
                  testId="voicemail-play"
                  label={isPlaying ? 'Pause' : 'Preview'}
                  sublabel="Listen"
                  variant="silver"
                  icon={isPlaying ? <PauseIcon big /> : <PlayIcon big />}
                />
                <BigWinampButton
                  onClick={submit}
                  testId="voicemail-submit"
                  label={phase === 'submitting' ? 'Sending…' : 'Send'}
                  sublabel={phase === 'submitting' ? 'Please wait' : 'Submit'}
                  variant="green"
                  disabled={phase === 'submitting'}
                  icon={<SendIcon big />}
                  fullWidth
                />
                <BigWinampButton
                  onClick={scrap}
                  testId="voicemail-scrap"
                  label="Redo"
                  sublabel="Re-record"
                  variant="silver"
                  icon={<EjectIcon big />}
                  disabled={phase === 'submitting'}
                />
              </>
            )}
          </div>

          {/* Decorative status pills (kept from the original skin) */}
          <div className="flex items-center gap-[3px] mt-2">
            <div className="flex-1" />
            <PillTab label={phase === 'recording' ? 'REC' : phase === 'stopped' ? 'READY' : 'IDLE'} active={phase === 'recording'} accent={phase === 'recording' ? 'red' : phase === 'stopped' ? 'green' : 'grey'} />
            <PillTab label="REPEAT" />
            <span
              aria-hidden
              className="ml-1 inline-flex items-center justify-center h-6 w-6"
              style={{ background: 'linear-gradient(180deg,#c6cbde,#7c85a3)', border: '1px solid #1a2035', borderRadius: 2, fontSize: 10, fontWeight: 900, color: '#33ff66', textShadow: '0 0 3px #33ff66' }}
              title="Outlaw"
            >
              ☠
            </span>
          </div>

          {blobUrl && (
            <audio
              ref={audioRef}
              src={blobUrl}
              onEnded={() => setIsPlaying(false)}
              onPause={() => setIsPlaying(false)}
              className="hidden"
            />
          )}
        </div>
      </div>

      {/* ============ Optional metadata + submit copy (below the skin) ============ */}
      {(phase === 'stopped' || phase === 'submitting') && (
        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Alias (optional)</label>
            <input
              type="text"
              value={alias}
              maxLength={80}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="How should we refer to you?"
              data-testid="voicemail-alias"
              className="w-full bg-black border border-zinc-800 focus:border-red-500 rounded px-3 py-2 text-white text-sm outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Written note (optional)</label>
            <textarea
              rows={2}
              maxLength={2000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Anything Ryan should know before listening"
              data-testid="voicemail-message"
              className="w-full bg-black border border-zinc-800 focus:border-red-500 rounded px-3 py-2 text-white text-sm outline-none"
            />
          </div>
          <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} className="hidden" aria-hidden />
        </div>
      )}

      {phase === 'idle' && (
        <div
          data-testid="voicemail-content-warning"
          className="mt-4 rounded-lg border border-yellow-500/50 bg-yellow-500/10 px-4 py-3"
        >
          <p className="text-yellow-300 text-[10px] font-black uppercase tracking-[0.3em] mb-1">
            Content notice
          </p>
          <p className="text-yellow-100/90 text-xs leading-relaxed">
            Your voicemail may be used in future Outlaw Media content - articles, videos,
            audio segments, social clips. By tapping RECORD you grant Outlaw Media permission
            to publish and reuse your message.
          </p>
        </div>
      )}

      {error && (
        <div data-testid="voicemail-error" className="mt-4 text-red-300 text-sm bg-red-900/30 border border-red-500/40 rounded px-3 py-2">
          {error}
        </div>
      )}

      {phase === 'done' && (
        <div data-testid="voicemail-done" className="mt-4 text-center py-4">
          <p className="text-emerald-400 text-[11px] font-black uppercase tracking-[0.3em] mb-1">Transmission complete</p>
          <p className="text-white text-lg font-black">Thanks. Ryan will listen.</p>
        </div>
      )}
    </div>
  );
};

/* ===================== Winamp bits ===================== */

/**
 * Chunky labelled transport button. Bigger tap target (56px tall), icon on
 * top + word label + tiny sublabel so no-one has to guess which one does
 * what. `variant` picks the colour theme.
 */
const BigWinampButton = ({ icon, label, sublabel, onClick, disabled, testId, variant = 'silver', pulsing, fullWidth }) => {
  const themes = {
    red: {
      bg: 'linear-gradient(180deg,#ff6d6d 0%,#c81c1c 60%,#7c0f0f 100%)',
      text: '#fff',
      shadow: '0 0 12px rgba(220,38,38,0.55)',
      inner: 'inset 1px 1px 0 rgba(255,255,255,0.55), inset -1px -1px 0 rgba(0,0,0,0.5)',
    },
    green: {
      bg: 'linear-gradient(180deg,#5cf07f 0%,#18a13a 60%,#0d5a1f 100%)',
      text: '#0a1f10',
      shadow: '0 0 12px rgba(52,211,113,0.55)',
      inner: 'inset 1px 1px 0 rgba(255,255,255,0.55), inset -1px -1px 0 rgba(0,0,0,0.4)',
    },
    silver: {
      bg: 'linear-gradient(180deg,#e6ebfa 0%,#a2acc9 60%,#5c6689 100%)',
      text: '#1a2035',
      shadow: '0 1px 3px rgba(0,0,0,0.4)',
      inner: 'inset 1px 1px 0 #fff, inset -1px -1px 0 #1a2035',
    },
  };
  const th = themes[variant] || themes.silver;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-label={label}
      className={`relative inline-flex flex-col items-center justify-center h-14 px-3 py-1 disabled:opacity-40 disabled:cursor-not-allowed ${fullWidth ? 'flex-1' : ''} ${pulsing ? 'animate-[winamp-big-pulse_1.1s_ease-in-out_infinite]' : ''}`}
      style={{
        minWidth: 96,
        background: th.bg,
        color: th.text,
        border: '1px solid #1a2035',
        borderRadius: 4,
        boxShadow: `${th.inner}, ${th.shadow}`,
        textShadow: variant === 'silver' ? '0 1px 0 rgba(255,255,255,0.55)' : '0 1px 1px rgba(0,0,0,0.35)',
        fontFamily: 'Verdana, sans-serif',
      }}
    >
      <span className="flex items-center gap-1.5">
        {icon}
        <span className="text-[13px] font-black uppercase tracking-widest leading-none">{label}</span>
      </span>
      {sublabel && (
        <span className="text-[9px] uppercase tracking-[0.25em] mt-0.5 opacity-90">{sublabel}</span>
      )}
      <style>{`
        @keyframes winamp-big-pulse {
          0%,100% { transform: scale(1); box-shadow: ${th.inner}, 0 0 12px rgba(220,38,38,0.55); }
          50%     { transform: scale(1.03); box-shadow: ${th.inner}, 0 0 22px rgba(220,38,38,0.85); }
        }
      `}</style>
    </button>
  );
};

const BitBadge = ({ label }) => (
  <span
    className="inline-flex items-center justify-center px-1 rounded-[2px]"
    style={{
      background: '#0a0e18',
      color: '#33ff66',
      textShadow: '0 0 3px #33ff66',
      boxShadow: 'inset 0 0 0 1px #1a2233',
      fontFamily: '"Courier New", monospace',
      height: 14,
    }}
  >
    {label}
  </span>
);

const TabBadge = ({ label }) => (
  <span
    className="inline-flex items-center px-1.5 rounded-[2px] gap-0.5"
    style={{
      background: 'linear-gradient(180deg,#c6cbde,#7c85a3)',
      border: '1px solid #1a2035',
      boxShadow: 'inset 1px 1px 0 rgba(255,255,255,0.7), inset -1px -1px 0 #1a2035',
      color: '#1a2035',
      height: 14,
      fontFamily: 'Verdana, sans-serif',
    }}
  >
    <span className="inline-block h-1.5 w-1.5" style={{ background: '#33ff66', boxShadow: '0 0 2px #33ff66' }} />
    {label}
  </span>
);

const PillTab = ({ label, active, accent }) => (
  <span
    className="inline-flex items-center px-1.5 rounded-[2px] gap-1 h-[16px]"
    style={{
      background: 'linear-gradient(180deg,#c6cbde,#7c85a3)',
      border: '1px solid #1a2035',
      boxShadow: 'inset 1px 1px 0 rgba(255,255,255,0.7), inset -1px -1px 0 #1a2035',
      color: '#1a2035',
      fontFamily: 'Verdana, sans-serif',
      fontSize: 9,
    }}
  >
    <span
      className="inline-block h-1.5 w-1.5"
      style={{
        background: accent === 'red' ? '#ff3838' : accent === 'green' ? '#33ff66' : '#5a6480',
        boxShadow: active && accent === 'red' ? '0 0 3px #ff3838' : (accent === 'green' ? '0 0 3px #33ff66' : 'none'),
      }}
    />
    {label}
  </span>
);

/* ===== chunky icons drawn in CSS/svg so they read like Winamp's ===== */
const sizedProps = (big) => (big ? { width: 22, height: 16 } : { width: 20, height: 14 });
const PrevIcon = ({ big } = {}) => (
  <svg viewBox="0 0 24 16" {...sizedProps(big)} fill="currentColor"><rect x="2" y="3" width="2" height="10" /><polygon points="22,3 6,8 22,13" /></svg>
);
const PlayIcon = ({ big } = {}) => (<svg viewBox="0 0 24 16" {...sizedProps(big)} fill="currentColor"><polygon points="4,3 20,8 4,13" /></svg>);
const PauseIcon = ({ big } = {}) => (<svg viewBox="0 0 24 16" {...sizedProps(big)} fill="currentColor"><rect x="6" y="3" width="4" height="10" /><rect x="14" y="3" width="4" height="10" /></svg>);
const StopIcon = ({ big } = {}) => (<svg viewBox="0 0 24 16" {...sizedProps(big)} fill="currentColor"><rect x="4" y="3" width="16" height="10" /></svg>);
const RecordIcon = ({ big } = {}) => (<svg viewBox="0 0 24 16" {...sizedProps(big)}><circle cx="12" cy="8" r={big ? 6 : 5} fill={big ? '#ffffff' : '#c81c1c'} /></svg>);
const SendIcon = ({ big } = {}) => (<svg viewBox="0 0 24 16" {...sizedProps(big)} fill="currentColor"><polygon points="4,3 20,8 4,13 8,8" /></svg>);
const EjectIcon = ({ big } = {}) => (<svg viewBox="0 0 24 16" {...sizedProps(big)} fill="currentColor"><polygon points="12,3 20,11 4,11" /><rect x="4" y="12" width="16" height="2" /></svg>);

export default VoicemailRecorder;
